import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { IconButton } from "@atlaskit/button-next/new";
import CrossIcon from "@atlaskit/icon/core/cross";
import { JiraIcon } from "@atlaskit/logo-next";
import { Flex } from "@atlaskit/primitives-next/compiled";

import { wacToken } from "@/libraries/design/foundations";
import { messages } from "@/templates/paid-landing/sections/cfind-4666-frictionless-first-look/messages.ts";

import { JiraFFLContentGenerationResponse } from "../../../../../libraries/bxp-web-stargate-client/src";

import { Background } from "./background";
import { PreviewTable } from "./preview-table";
import { TableRowProps } from "./preview-table/table-row";

export interface PaidJiraAiPreviewModalProps {
    onClose?: () => void;
    children?: React.ReactNode;
    cgsData?: JiraFFLContentGenerationResponse;
    cgsDataLoading?: boolean;
}

const containerStyle = css({
    position: "fixed",
    insetInlineStart: 0,
    insetBlockStart: 0,
    width: "100vw",
    height: "100vh",
    background: wacToken("color.neutral.light0"),
    zIndex: 20,
    padding: "20px 28px"
});

const modalOverlayStyle = css({
    position: "fixed",
    inset: 0,
    width: "100vw",
    height: "100vh",
    zIndex: 30,
    // eslint-disable-next-line local/ensure-wac-token-usage
    background: "rgba(30, 41, 59, 0.3)",
    backdropFilter: "blur(2px)",
    pointerEvents: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const backgroundStyle = css({
    position: "fixed",
    inset: 0,
    width: "100vw",
    height: "100vh",
    zIndex: 10,
    background: wacToken("color.neutral.light0"),
    pointerEvents: "none", // Prevent interaction
    padding: "20px 28px"
});

export const PaidJiraAiPreviewModal = ({
    onClose,
    children,
    cgsData,
    cgsDataLoading
}: PaidJiraAiPreviewModalProps) => {
    const intl = useIntl();
    const [formattedCgsData, setFormattedCgsData] = useState<
        Omit<TableRowProps, "isLastRow">[]
    >([]);

    useEffect(() => {
        if (!cgsDataLoading && cgsData) {
            const epicData = cgsData.sampleIssues.epic;
            const workItemsData = cgsData.sampleIssues.childWorkItems;
            const finalData: Omit<TableRowProps, "isLastRow">[] = [];
            if (epicData) {
                finalData.push({
                    type: "epic",
                    summary: epicData.epicName,
                    status: "to do",
                    dueDate: new Date(),
                    priority: "high",
                    assignee: "person"
                });
            }

            if (workItemsData && workItemsData.length > 0) {
                workItemsData.forEach((item) => {
                    finalData.push({
                        type: "task",
                        summary: item.summary,
                        status: "in progress",
                        dueDate: new Date(),
                        priority: "medium",
                        assignee: "person"
                    });
                });
            }

            setFormattedCgsData(finalData);
        }
    }, [cgsData, cgsDataLoading]);

    const closeMessage = intl.formatMessage(messages.close);

    // Only prevent scroll if modal is open
    useEffect(() => {
        if (!children) return;
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [children]);

    // If no children, render only the background content (no modal overlay)
    if (!children) {
        return (
            <div css={containerStyle}>
                <Background />
                <Flex alignItems="center" justifyContent="space-between">
                    <JiraIcon appearance="brand" shouldUseNewLogoDesign />
                    <IconButton
                        shape="circle"
                        icon={CrossIcon}
                        label={closeMessage}
                        appearance="default"
                        onClick={onClose}
                    />
                </Flex>
                <PreviewTable rows={cgsDataLoading ? [] : formattedCgsData} />
            </div>
        );
    }

    // If children exist, render modal overlay and background
    return (
        <>
            <div css={backgroundStyle} className="modal-background">
                <Background />
                <Flex alignItems="center" justifyContent="space-between">
                    <JiraIcon appearance="brand" shouldUseNewLogoDesign />
                </Flex>
                <PreviewTable rows={cgsDataLoading ? [] : formattedCgsData} />
            </div>
            <div
                css={modalOverlayStyle}
                className="modal-overlay"
                role="dialog"
                aria-modal="true"
                aria-label="Paid Jira AI Preview Modal"
            >
                {children}
            </div>
        </>
    );
};
